import { observable, action, computed } from 'mobx'
import Parse from 'parse'
import _ from 'lodash'
import moment from 'moment'
import GolfCourseStore from './GolfCourseStore'
import DatesStore from './DatesStore'
import ConfigStore from './ConfigStore'
import UserStore from './UserStore'

class BookingStore {

    @observable activeBookings
    @observable userFreeBookings
    @observable maxBookingCount
    @observable bookingCount
    @observable maxRemainingStockBookingCount
    @observable bookingRemainingStockCount
    @observable chooseDateModalError
    @observable bookingModalError
    @observable bookingsPerDate
    @observable golfCourseFreeSlots

    // Data for future Booking
    @observable bookingDate
    @observable golfCourse
    @observable slots
    @observable isInvite

    // Data which is shared between screens of bookingModal
    @observable screenNumber
    @observable includeMyself

    constructor() {
        this.activeBookings = null
        this.cancledBooking = null
        this.userFreeBookings = null
        this.chooseDateModalError = null
        this.bookingModalError = null
        this.bookingsPerDate = null
        this.golfCourseFreeSlots = null
        this.maxBookingCount = null
        this.bookingCount = null
        this.maxRemainingStockBookingCount = null
        this.bookingRemainingStockCount = null

        this.bookingDate = null
        this.golfCourse = null
        this.slots = 1
        this.isInvite = false
        this.guests = []

        this.screenNumber = 1
        this.includeMyself = false
    }

    @computed get isActiveBookings() {
        return this.activeBookings && this.activeBookings[0].objectId ? true : false
    }

    @computed get isLoading() {
        return this.isLoading ? true : false
    }

    @computed get isChooseDateModalError() {
        return this.chooseDateModalError ? true : false
    }

    @computed get isBookingModalError() {
        return this.bookingModalError ? true : false
    }

    @computed get isBookingsPerDate() {
        return this.bookingsPerDate ? true : false
    }

    @computed get isGolfCourse() {
        return this.golfCourse ? true : false
    }

    @computed get isGolfCourseAndDate() {
        return this.bookingDate && this.golfCourse ? true : false
    }

    @computed get isUserFreeBookings() {
        return this.userFreeBookings || this.userFreeBookings === 0 ? true : false
    }

    @action setActiveBookings = (activeBookings) => {
        this.activeBookings = activeBookings
    }

    @action sortActiveBookings = () => {
        if (this.activeBookings) {
            this.activeBookings = _.orderBy(this.activeBookings,   ['bookingDate'],   ['asc'])
        }
    }

    @action setUserFreeBookings = (userFreeBookings) => {
        this.userFreeBookings = userFreeBookings
    }

    @action setChooseDateModalError = (chooseDateModalError) => {
        this.chooseDateModalError = chooseDateModalError
    }

    @action setBookingModalError = (bookingModalError) => {
        this.bookingModalError = bookingModalError
    }

    @action setBookingsPerDate = (bookingsPerDate) => {
        this.bookingsPerDate = bookingsPerDate
    }

    @action setGolfCourse = (golfCourse) => {
        this.golfCourse = golfCourse
    }

    @action setDate = (bookingDate) => {
        this.bookingDate = bookingDate
    }

    @action setSlots = (slots) => {
        this.slots = slots
    }

    @action resetFutureBooking = () => {
        this.setDate(null)
        this.setGolfCourse(null)
        this.setSlots(1)
        this.setChooseDateModalError(null)
        this.setBookingModalError(null)
        this.isInvite = false
        this.includeMyself = false
        this.guests = []
    }

    @action setSlots = (slots) => {
        this.slots = slots
    }

    @action setGolfCourseFreeSlots = (golfCourseFreeSlots) => {
        this.golfCourseFreeSlots = golfCourseFreeSlots
    }

    @action setScreenNumber = (screenNumber) => {
        this.screenNumber = screenNumber
    }

    @action setSlots = (slots) => {
        this.slots = slots
    }

    @action setGuests = (guests) => {
        this.guests = guests
    }

    @action setIsInvite = (isInvite) => {
        this.isInvite = isInvite
    }

    @action setIncludeMyself = (includeMyself) => {
        this.includeMyself = includeMyself
    }

    getActiveBookings = () => {

        this.activeBookings = null
        let activeBookings = []

        const today = new Date().toISOString().substr(0, 10)

        let bookingQuery = new Parse.Query("Booking")
        bookingQuery.include("user")
        bookingQuery.equalTo("isCanceled", false)
        bookingQuery.greaterThanOrEqualTo("bookingDate", today)

        bookingQuery.each(booking => {
            if (UserStore.isLoggedIn && (booking.toJSON().user.company.objectId === UserStore.me.company.objectId)) {
                activeBookings.push(booking.toJSON())
            }
        }).then(res => {
            if (activeBookings.length === 0) {
                this.setUserFreeBookings(UserStore.maxBookings)
                this.getCompanyFreeSlots(UserStore.me)
            } else {
                this.setActiveBookings(activeBookings)
                this.sortActiveBookings()
                this.getUserFreeBookings()
            }
        }).catch(err => {
            console.error("error active bookings", err)
        })
    }

    getCompanyFreeSlots = (me) => {
        Parse.Cloud.run('companySlotCount', {
            currentUserId: me.objectId
        }).then(result => {
            this.bookingCount = result.bookingCount
            this.maxBookingCount = result.maxBookingCount
            this.bookingRemainingStockCount = result.bookingRemainingStockCount
            this.maxRemainingStockBookingCount = result.maxRemainingStockBookingCount
        }).catch(error => {
            console.error("error companyRole", error)
        })
    }

    getUserFreeBookings = () => {
        this.getCompanyFreeSlots(UserStore.me)
            // Filter all active bookings where isRemainingStock === false
        let bookings = 0

        this.activeBookings.map(booking => {
            if (booking.isRemainingStock === false) {
                bookings++
            }
            // If you remove this return, a warning will appear.
            // See last comment on https://github.com/eslint/eslint/issues/8539 for more info
            return void 0
        })
        this.setUserFreeBookings(UserStore.maxBookings - bookings)
    }

    // Returns a Promise with an array of new Invite ParseObjects
    createInvites(newBookingObject) {
        return new Promise(resolve => {
            let invites = []
            const InviteParseObj = Parse.Object.extend("Invite")

            this.guests.map(guest => {
                let newInvite = new InviteParseObj()
                newInvite.set("booking", newBookingObject)
                newInvite.set("firstname", guest.firstname)
                newInvite.set("lastname", guest.lastname)
                newInvite.set("genderTitle", guest.genderTitle)
                newInvite.set("title", guest.title)
                newInvite.set("company", guest.company)
                newInvite.set("phone", guest.phone)
                newInvite.set("email", guest.email)
                newInvite.save().then(savedInvite => {
                    invites.push(savedInvite)
                    if (invites.length === this.guests.length) { resolve(invites) }
                }).catch(error => console.error("Could not save invite", error))

                // If you remove this return, a warning will appear.
                // See last comment on https://github.com/eslint/eslint/issues/8539 for more info
                return void 0
            })
        });
    }

    createBooking(date, golfCourseId, slots) {
        let isRemainingStock = this.isRemainingStock(date)

        let golfCourseObject = null
        let golfCourseQuery = new Parse.Query("GolfCourse")

        golfCourseQuery.equalTo("objectId", golfCourseId)
        golfCourseQuery.include("hotel")
        golfCourseQuery.each(result => {
            golfCourseObject = result
        }).then(() => {

            const BookingParseObj = Parse.Object.extend("Booking")
            let newBooking = new BookingParseObj()

            newBooking.set("bookingDate", date)
            newBooking.set("bookingDateObj", new Date(date))
            newBooking.set("golfCourse", golfCourseObject)
            newBooking.set("user", Parse.User.current())
            newBooking.set("isCanceled", false)
            newBooking.set("isRemainingStock", isRemainingStock)
            newBooking.set("isInvite", this.isInvite)
            newBooking.set("slots", slots)

            return newBooking.save()

        }).then(newBookingObject => {
            if (this.isInvite) {
                this.createInvites(newBookingObject).then(invites => {
                    let relation = newBookingObject.relation("invites")
                    relation.add(invites)
                    newBookingObject.save()
                }).catch(error => console.error("createInvites error", error))
            }

            this.setScreenNumber(3)
            this.rerender()
        }).catch(error => {
            if ((_.has(error.message.message) ? error.message.message.message.errorName : 'error') === "maxBookingsPerGolfCourseError") {
                this.setBookingModalError("Sie können nicht mehrere Buchungen pro Golfclub tätigen.")
            }
            if (error.message.errorName === "companySlotCountError") {
                this.setBookingModalError(error.message.message)
            } else {
                this.setBookingModalError("Keine Buchung möglich - bitte berücksichtigen Sie das Regelwerk.")
            }
            this.rerender()
            console.error("createBooking error", error)
        })
    }

    cancelBooking(bookingId) {

        let bookingObject = null
        let bookingQuery = new Parse.Query("Booking")

        bookingQuery.equalTo("objectId", bookingId)
        return bookingQuery.each(result => {
            bookingObject = result
        }).then(() => {
            bookingObject.set("isCanceled", true);
            return bookingObject.save().then(res => {
                this.cancledBooking = bookingObject.toJSON()
            })
        });
    }

    isRemainingStock(bookingDate) {

        const bookingIsToday = bookingDate === DatesStore.today ? true : false
        const bookingIsTomorrow = bookingDate === DatesStore.tomorrow ? true : false

        const currentTime = moment().format("HH:mm:ss")
        const isAfterRestrictionTime = currentTime > ConfigStore.noRestrictionTime

        if (bookingIsToday || (bookingIsTomorrow && isAfterRestrictionTime)) {
            return true
        }
        return false
    }

    alreadyBooked = (date, golfCourse) => {
        let alreadyBooked = false
        let bookingsOnDayCount = 0
        let remainingStockBookingsOnDayCount = 0
        if (this.isActiveBookings) {
            this.activeBookings.map(booking => {
                if (booking.bookingDate === date && booking.golfCourse.shortName === golfCourse.shortName) {
                    if (booking.isRemainingStock) {
                        remainingStockBookingsOnDayCount++
                    } else {
                        bookingsOnDayCount++
                    }
                }
                return true
            })
        }

        if ((UserStore.maxBookingsPerDay - bookingsOnDayCount) < 1) {
            alreadyBooked = true
        }

        if (((UserStore.maxRemainingStockBookings - remainingStockBookingsOnDayCount) < 1)) {
            alreadyBooked = true
        }

        return alreadyBooked
    }

    rerender() {
        this.getActiveBookings()
        UserStore.getCompanyRemainingCoins()
        GolfCourseStore.getAllBookings()
    }
}

const store = new BookingStore()
export default store



// WEBPACK FOOTER //
// /src/stores/BookingStore.js